
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import styles from './AuditLogs.module.css'
import { globalContext } from '../../Provider/AppProvider';
import { GET_NOTIFICATIONS } from '../../components/shared-components/notification/api/NotificationQuery';
import { useLazyQuery } from '@apollo/client';
import { getFormattedDate } from '../../helper';
import AuditLogitixLogs from '../../components/shared-components/auditLogs/AuditLogitixLogs';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { CircularProgress } from '@mui/material';
import ErrorDashboard from '../ErrorDashboard/ErrorDashboard';

const AuditLogs = () => {

  const {globalState,handleGlobalState} = useContext<any>(globalContext)
  localStorage.removeItem('ticketsTabIndex');
  localStorage.removeItem('settingsTabIndex');
  const [activeTab, setActiveTab] = useState(0);

  const [tempRowsPerPage, setTempRowsPerPage] = useState();
  const [rowsPerPage, setRowsPerPage] = useState(parseInt(tempRowsPerPage ? tempRowsPerPage : '') || 10);
  const [currentPage, setCurrentPage] = useState(1);
  const [checking,setChecking]=useState<boolean>(false);
  

  const [auditLogsList,setAuditLogsList] = useState<any>({ logitix_audit_logs: [] });
  const [searchText, setSearchText] = useState<string>('');
  const [refreshLoading, setRefreshLoading] = useState<boolean>(false);
  const [queryError, setQueryError] = useState<string | null>(null);

  const [theme, setTheme] = useState<string>(() => {
    try {
      return localStorage.getItem('theme') || (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
    } catch (e) {
      return 'light';
    }
  });

  const refetchFn = useRef<(() => Promise<any>) | null>(null);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.setAttribute('data-theme', 'dark');
      root.classList.add('dark-mode');
    } else {
      root.setAttribute('data-theme', 'light');
      root.classList.remove('dark-mode');
    }
    try { localStorage.setItem('theme', theme); } catch (e) {}
  }, [theme]);

  const handleRefresh = async () => {
    if (activeTab !== 1) {
      setQueryError('Refresh is available only on Super User - Audit Logs tab');
      return;
    }
    if (!refetchFn.current) {
      setQueryError('Audit logs refresh not ready yet. Please wait and try again.');
      return;
    }
    setRefreshLoading(true);
    setQueryError(null);
    try {
      await refetchFn.current();
    } catch (e: any) {
      console.error('Failed to refresh audit logs', e);
      setQueryError(e?.message || 'Refresh failed');
    } finally {
      setRefreshLoading(false);
    }
  };

  const toggleTheme = () => setTheme(prev => prev === 'dark' ? 'light' : 'dark');

  const [columnTypes, setColumnTypes] = useState('');
 


  const [sortType, setSortType] = useState('desc');
  const [selected, setSelected] = useState<number[]>([]);
  const date = getFormattedDate(new Date());

  const filteredAuditLogsList = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return auditLogsList;

    const filteredRows = (auditLogsList?.logitix_audit_logs || []).filter((row: any) => {
      const values = [
        row?.event?.e_name,
        row?.league?.l_name,
        row?.api_type,
        row?.Action,
        row?.status,
        row?.venue,
        row?.ticket_placement?.ticket?.user?.u_full_name,
        row?.user?.u_full_name,
        row?.user?.u_original_email,
        row?.user?.u_email_id,
        row?.old_listing_price?.toString?.(),
        row?.updated_listing_price?.toString?.(),
        Array.isArray(row?.ticket_placements) ? row.ticket_placements.map((p: any) => `${p?.section || ''} ${p?.row || ''} ${p?.seat_no || ''}`).join(' ') : '',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return values.includes(query);
    });

    return { ...auditLogsList, logitix_audit_logs: filteredRows };
  }, [auditLogsList, searchText]);

  const [getNotification] = useLazyQuery(GET_NOTIFICATIONS,{
    fetchPolicy:"no-cache",
    onCompleted:(data)=>{
      const tempState = {...globalState}
      tempState.notificationCount.recent = data?.currentCount?.aggregate?.count
      tempState.notificationCount.past = data?.pastCount?.aggregate?.count
      handleGlobalState(tempState)
    }
  })

  useEffect(()=>{
    const tempValue = {...globalState};
    if(globalState.globalSearch.affiliateUsersClicked){
      tempValue.globalSearch.searchtext = "";
      tempValue.globalSearch.affiliateUsersClicked = false;
    }
    if(globalState.notification.notificationClicked){
      tempValue.notification.notificationClicked = false;
      tempValue.notification.tp_id_array=null;
      tempValue.globalSearch.searchtext = "";
      tempValue.notification.text = "";
      tempValue.notification.ticketId =null;
      tempValue.notification.tpId = null;
    }else if(globalState.error.errorClicked){
      tempValue.error.errorClicked = false;
      tempValue.globalSearch.searchtext = "";
      tempValue.error.text = "";
      tempValue.error.approximateTpArray = null
      tempValue.error.ticketPlacementId = null
      tempValue.error.status =""
    }
    handleGlobalState(tempValue);
    getNotification({
      variables:{
        todayDate: date,
        userId: globalState?.profile?.id
      }
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  /* For get the ManageTicket table headers */
  const GenerateAuditLogsHeadCells = ()=>{
    const id =  ["event_name","league_name","api_type","action","status","ticket_placements","venue","old_listing_price","updated_listing_price","customer_name","update_user"]; 
    const labels =["Event Name","League Name","API Type","Action","Status","Ticket Placements,Section,Row, Seat", "Venue","Old Listing Price","Updated Listing Price","Customer Name","Update User"];
    const headCells = id.map((id, index) => ({
        id,
        label: labels[index],
      }));
      return headCells;
}

const handleChangeRowsPerPage = (event: any) => {
  setRowsPerPage(parseInt(event.target.value, 10));
  setTempRowsPerPage(event.target.value);
  setSelected([]);
  setCurrentPage(1);
};

const handlePageChange = (_event: React.ChangeEvent, newPage: number) => {
  setCurrentPage(newPage);
  setSelected([]);
  setChecking(false)
};

const handleTabSelect = (tabIndex: number) => {
  setActiveTab(tabIndex);
  setSelected([]);
  setCurrentPage(1);
  setChecking(false);
};

  return (
      <div className={styles.pageContainer}>
        <div className={styles.pageHeader}>
          <div className={styles.pageTitleContainer}>
             
            <h1 className={styles.pageTitle}>Audit Logs</h1>
          </div>
          <div className={styles.headerControls}>
            <div className={styles.searchInputContainer}>
              <SearchRoundedIcon style={{ fontSize: 18, color: 'var(--audit-text-muted)' }} />
              <input
                type="text"
                className={styles.searchInput}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search audit logs"
              />
            </div>
            <button
              onClick={handleRefresh}
              aria-label="Refresh audit logs"
              title="Refresh"
              className={styles.iconButton}
              disabled={refreshLoading}
            >
              {refreshLoading ? <CircularProgress size={18} color="inherit" /> : <RefreshRoundedIcon style={{fontSize:18}} />}
            </button>
            <button onClick={toggleTheme} aria-label="Toggle theme" title="Toggle theme" className={styles.iconButton}>
              {theme === 'dark' ? <DarkModeOutlinedIcon style={{fontSize:18}} /> : <LightModeOutlinedIcon style={{fontSize:18}} />}
            </button>
          </div>
        </div>
        
        <div className={styles.tabHeaderRow}>
          <div className={styles.tabContainer}>
            <button
              type="button"
              className={`${styles.tab} ${activeTab === 0 ? styles.activeTab : ""}`}
              onClick={() => handleTabSelect(0)}
            >
              Mail Parser Logs
            </button>
            <button
              type="button"
              className={`${styles.tab} ${activeTab === 1 ? styles.activeTab : ""}`}
              onClick={() => handleTabSelect(1)}
            >
              Super User - Audit Logs
            </button>
          </div>
      
        </div>
        
        {activeTab === 0 ? (
          <div className={styles.tabPanel}>
            <ErrorDashboard />
          </div>
        ) : (
          <div className={styles.tabPanel}>
            {queryError && <div className={styles.errorMessage}>{queryError}</div>}
            <AuditLogitixLogs
              rowsPerPage={rowsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              checking={checking}
              setChecking={setChecking}
              auditLogsList={auditLogsList}
              setAuditLogsList={setAuditLogsList}
              columnTypes={columnTypes}
              setColumnTypes={setColumnTypes}
              sortType={sortType}
              setSortType={setSortType}
              selected={selected}
              setSelected={setSelected}
              GenerateAuditLogsHeadCells={GenerateAuditLogsHeadCells}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handlePageChange={handlePageChange}
              setRefetch={(fn: any) => { refetchFn.current = fn; }}
              setQueryLoading={setRefreshLoading}
              setQueryError={setQueryError}
              filteredAuditLogsList={filteredAuditLogsList}
            />
          </div>
        )}
      </div>
  );
}

export default AuditLogs;